import React, { useCallback, useMemo } from 'react';
import classnames from 'classnames';
import { Pane, Flex, ToggleButton } from '@leverege/ui-elements';
import { ValueText, LabelText, DetailUtil, Attributes } from '@leverege/ui-attributes';
import I18N from '@leverege/i18n';
import Model from "../model/BucketAggTextViewModel.js";
import { AggClickActionFactory, AggModelFactory } from "../Factory.js";
import S from './BucketAggTextView.css';
import { buildVariant } from "../../util/index.js";
import FiltersAggModel from "../model/FiltersAggModel.js";
import { FilterSourcesModel } from "../../../../filters/index.js";
import BucketAggTextItem from "./BucketAggTextItem.js";
import { getEnumValues, getResourceEnumValues } from "./Util.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const {
  t
} = I18N.ns('molten.dataViewers.aggregations.BucketAggTextView');
export default function BucketAggTextView(props) {
  const {
    value,
    data,
    className,
    vertical,
    aggModel,
    queryModel,
    context,
    hasHeaders = true
  } = props;
  const {
    clientProps: {
      objectType,
      delegate,
      filters
    }
  } = context;
  const metadatas = FilterSourcesModel.getMetadata(filters);
  const filterBarFilterMetadata = metadatas?.filterBar?.filter;
  let {
    variant
  } = props;
  const attribute = Attributes.getAttribute(aggModel?.attrName, objectType);
  const buckets = useMemo(() => {
    return aggModel.type === FiltersAggModel.TYPE ? Object.keys(data.buckets).map(key => {
      return {
        key,
        count: data.buckets[key].count
      };
    }) : getBuckets(data.buckets, attribute, Model.getShowAllEnumValues?.(value), delegate);
  }, [aggModel, data.buckets, attribute, value, delegate]);
  const onClickBucket = useCallback(({
    data,
    value
  }) => {
    const action = AggClickActionFactory.get(value ? queryModel : 'aggregation.noFilter');
    action?.onClick(data, {
      ...context,
      queryModel
    });
  }, [queryModel, context]);

  // There may be a root level variant or the view itself may have a variant.
  // Go with the lowest level.
  variant = Model.getVariant(value) || variant;
  if (!data) {
    return null;
  }
  const AggModel = AggModelFactory.get(aggModel);
  const hLStyle = DetailUtil.getStyle(value?.header?.label);
  const vLStyle = {
    ...DetailUtil.getStyle(value?.value?.label),
    whiteSpace: 'nowrap'
  };
  const vTStyle = DetailUtil.getStyle(value?.value?.text);
  const headerLabel = Model.getHeaderLabel(value);
  return /*#__PURE__*/_jsx(Pane, {
    variant: buildVariant(variant, 'Item'),
    className: classnames(className, S.pane),
    children: /*#__PURE__*/_jsxs(Flex, {
      variant: buildVariant(variant, 'ContainerFlex', 'colMStart'),
      className: S.colFlex,
      children: [hasHeaders && /*#__PURE__*/_jsx(LabelText, {
        variant: buildVariant(variant, 'Title'),
        style: {
          ...hLStyle,
          visibility: headerLabel ? 'visible' : 'hidden'
        },
        children: headerLabel || 'M'
      }), /*#__PURE__*/_jsx(Flex, {
        variant: vertical ? 'aggBucketVertical' : 'aggBucketHorizontal',
        style: {
          flexWrap: vertical ? 'wrap' : undefined,
          width: vertical ? '100%' : undefined,
          flexGrow: 1
        },
        children: buckets.length > 0 ? buckets.map(bucket => {
          const key = AggModel.formatBucketKey(bucket, aggModel, {
            trueValue: Model.getTrueValue(value),
            falseValue: Model.getFalseValue(value),
            objectType,
            delegate
          });
          const name = AggModel.formatBucketName?.(bucket, aggModel, {
            trueValue: Model.getTrueValue(value),
            falseValue: Model.getFalseValue(value),
            objectType,
            delegate
          }) || key;
          return /*#__PURE__*/_jsx(BucketAggTextItem, {
            bucket: bucket,
            value: value,
            queryModel: queryModel,
            objectType: objectType,
            delegate: delegate,
            context: context,
            variant: variant,
            onClickBucket: onClickBucket,
            vLStyle: vLStyle,
            vTStyle: vTStyle,
            filter: filterBarFilterMetadata,
            bucketName: name
          }, key);
        }) : /*#__PURE__*/_jsx(ToggleButton, {
          disabled: true,
          variant: buildVariant(variant),
          children: /*#__PURE__*/_jsxs(Flex, {
            className: S.bucket,
            variant: buildVariant(variant, 'BucketFlex', 'colLStart'),
            children: [Model.getValueLabelVisible(value) && /*#__PURE__*/_jsx(LabelText, {
              variant: buildVariant(variant, 'Label'),
              style: vLStyle,
              children: t('noData')
            }), /*#__PURE__*/_jsx(ValueText, {
              variant: buildVariant(variant, 'Value'),
              style: vTStyle,
              children: "-"
            })]
          })
        })
      })]
    })
  });
}
function getBuckets(buckets, attribute, showAllEnumValues, delegate) {
  const values = new Map();
  for (let i = 0; i < buckets.length; i++) {
    values.set(buckets[i].key, buckets[i]);
  }
  if (showAllEnumValues) {
    let enumValues;
    if (attribute?.valueType === 'enum') {
      enumValues = getEnumValues(attribute.source.attribute.model.enumType, attribute.source.attribute.blueprint.model.projectId);
    } else if (attribute?.valueType === 'resourceEnum') {
      enumValues = getResourceEnumValues(attribute.source.attribute.model.enumType, delegate.systemId);
    }
    if (enumValues) {
      for (let i = 0; i < enumValues.length; i++) {
        const enumValue = enumValues[i];
        const match = buckets.find(bucket => bucket.key === enumValue.value);
        if (!match) {
          values.set(enumValue.value, {
            key: enumValue.value,
            count: 0
          });
        }
      }
    }
  }
  return Array.from(values.values());
}